<template>
  <v-progress-circular v-if="loading" color="white" :size="128" :width="8" />
  <v-responsive v-else :loading="loading" aspect-ratio="16/9">
    <svg
      class="room-svg"
      viewBox="0 0 1920 1080"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id="activeClip">
          <path v-if="activeMenu === 'decor'" :d="paths.decor" />
          <path v-else-if="activeMenu === 'sofa'" :d="paths.sofa" />
          <path v-else-if="activeMenu === 'tv'" :d="paths.tv" />
          <path v-else-if="activeMenu === 'furniture'" :d="paths.furniture" />
        </clipPath>
        <filter id="blurFilter">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>
      <image
        href="@/assets/livingroom/livingroom.png"
        width="1920"
        height="1080"
        :filter="isMenuOpen ? 'url(#blurFilter)' : ''"
      />
      <image
        href="@/assets/livingroom/livingroom.png"
        width="1920"
        height="1080"
        v-if="isMenuOpen && activeMenu"
        :clip-path="'url(#activeClip)'"
      />
      <path
        ref="decorActivator"
        :d="paths.decor"
        class="hotspot"
        tabindex="0"
        :class="{ blurred: isMenuOpen && activeMenu !== 'decor', active: decorMenu, disable: isMenuOpen }"
      />
      <path
        ref="furnitureActivator"
        :d="paths.furniture"
        class="hotspot"
        tabindex="0"
        :class="{ blurred: isMenuOpen && activeMenu !== 'furniture', active: furnitureMenu, disable: isMenuOpen }"
      />
      <path
        ref="tvActivator"
        :d="paths.tv"
        class="hotspot"
        :class="{ blurred: isMenuOpen && activeMenu !== 'tv', active: tvMenu, disable: isMenuOpen }"
        tabindex="0"
      />
      <path
        ref="sofarActivator"
        :d="paths.sofa"
        class="hotspot"
        :class="{ blurred: isMenuOpen && activeMenu !== 'sofa', active: sofaMenu, disable: isMenuOpen }"
        tabindex="0"
      />
    </svg>
    <item-menu
      v-if="decor"
      v-model:menu="decorMenu"
      :items="decor.items"
      :activator="decorActivator"
      @save="(payload) => saveItems(decor.id, payload.newItems, payload.updatedItems, payload.deletedItems)"
    />
    <item-menu
      v-if="sofa"
      v-model:menu="sofaMenu"
      :items="sofa.items"
      :activator="sofarActivator"
      @save="(payload) => saveItems(sofa.id, payload.newItems, payload.updatedItems, payload.deletedItems)"
    />
    <item-menu
      v-if="furniture"
      v-model:menu="furnitureMenu"
      :items="furniture.items"
      :activator="furnitureActivator"
      @save="(payload) => saveItems(furniture.id, payload.newItems, payload.updatedItems, payload.deletedItems)"
    />
    <item-menu
      v-if="tv"
      v-model:menu="tvMenu"
      :items="tv.items"
      :activator="tvActivator"
      @save="(payload) => saveItems(tv.id, payload.newItems, payload.updatedItems, payload.deletedItems)"
    />
  </v-responsive>
</template>
<script setup>
import { computed, ref } from 'vue';
import paths from '@/assets/livingroom/paths';
import ItemMenu from '../base/ItemMenu.vue';
import RoomService from '@/services/RoomService';
import useApiCall from '@/composables/useApiCall';
import ItemGroupService from '@/services/ItemGroupService';

const decorActivator = ref(null);
const sofarActivator = ref(null);
const furnitureActivator = ref(null);
const tvActivator = ref(null);

const decor = computed(() => livingRoom.value?.itemGroups.find((g) => g.name === 'decor'));
const sofa = computed(() => livingRoom.value?.itemGroups.find((g) => g.name === 'sofa'));
const furniture = computed(() => livingRoom.value?.itemGroups.find((g) => g.name === 'furniture'));
const tv = computed(() => livingRoom.value?.itemGroups.find((g) => g.name === 'tv'));

const {
  data: livingRoom, loading, error, callApi: fetchLivingRoom,
} = useApiCall(
  () => RoomService.getRoom(1),
  (res) => res,
);

const decorMenu = ref(false);
const sofaMenu = ref(false);
const furnitureMenu = ref(false);
const tvMenu = ref(false);

const activeMenu = computed(() => {
  if (decorMenu.value) return 'decor';
  if (sofaMenu.value) return 'sofa';
  if (furnitureMenu.value) return 'furniture';
  if (tvMenu.value) return 'tv';
  return null;
});

const isMenuOpen = computed(() => !!activeMenu.value);

async function saveItems(groupId, newItems, updatedItems, deletedItems) {
  try {
    let updatedGroup = null;
    if (deletedItems?.length > 0) {
      updatedGroup = await ItemGroupService.deleteItems(
        groupId,
        deletedItems.map((i) => i.id),
      );
    }
    if (updatedItems?.length > 0) {
      updatedGroup = await ItemGroupService.updateItems(groupId, updatedItems);
    }
    if (newItems?.length > 0) {
      updatedGroup = await ItemGroupService.addItems(groupId, newItems);
    }
    if (updatedGroup) {
      const groups = livingRoom.value.itemGroups;
      const index = groups.findIndex((g) => g.id === groupId);
      if (index !== -1) {
        groups.splice(index, 1, updatedGroup);
        livingRoom.value.itemGroups[index].items.sort((a, b) => a.rank - b.rank);
      }
    }
  } catch (e) {
    console.error(e);
    await fetchLivingRoom();
  }
}
</script>

<style scoped>
.room-wrapper {
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  height: auto;
}

.room-svg {
  width: 100%;
  height: auto;
  display: block;
}

.hotspot {
  fill: rgba(250, 250, 215, 0);
  stroke: rgba(255, 255, 255, 0.3);
  stroke-width: 2;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hotspot:hover,
.hotspot:focus,
.hotspot.active {
  animation: pulse 1s infinite alternate;
  outline: none;
}

@keyframes pulse {
  0% {
    fill: rgba(247, 214, 160, 0.3);
    stroke: rgba(186, 136, 0, 0.3);
    stroke-width: 2;
  }
  100% {
    fill: rgba(186, 136, 0, 0.25);
    stroke: rgba(247, 214, 160, 0.5);
    stroke-width: 4;
  }
}

.blurred {
  filter: blur(3px);
  pointer-events: none;
  transition: filter 0.3s;
}

.disable{
  pointer-events: none;
}
</style>
